import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Divider, Stack, Typography } from '@mui/material';
import {
    Booking,
    EventItem,
    useDayProductEvents,
    useMoveBooking,
} from '@travelity/api';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Button, LoadingOverlay } from '@travelity/ui';
import { format, startOfDay } from 'date-fns';
import { useForm } from '@travelity/form';
import { useToast } from '../../hooks';
import { DailyViewHeader } from '../../views/events/components/daily-view-header';
import { EventItemMinimal } from '../event-item-minimal';
import { FilterItem } from '../filters/components/filter-item';
import { FilterTypes } from '../filters/filters.types';
import { ReactComponent as LoopSvg } from '../../assets/loop.svg';
import EventItemMinimalSkeleton from '../event-item-minimal/event-item-minimal-skeleton';
import { PriceSync } from '../price-sync';

export interface MoveBookingModalProps {
    handleCancel: () => void;
    handleConfirm: () => void;
    booking: Booking;
    open: boolean;
}

const dateFilter = {
    type: FilterTypes.DATE,
    name: 'date',
    label: 'Select other date',
};

const MoveBookingModal: React.FC<MoveBookingModalProps> = ({
    handleCancel,
    handleConfirm,
    booking,
    open,
}) => {
    const { Form, watch, setValue } = useForm({
        defaultValues: {
            date: startOfDay(booking.date.start),
        },
        mode: 'onChange',
    });
    const [syncPricing, setSyncPricing] = useState(true);
    const { showError } = useToast();
    const date = watch('date');

    const [selected, setSelected] = React.useState<EventItem | null>(null);

    useEffect(() => {
        if (open) {
            setSelected(null);
            setValue('date', startOfDay(booking.date.start));
        }
    }, [open, booking]);

    const { data: allEvents, isFetching } = useDayProductEvents(
        useMemo(
            () => ({
                day: date.getTime(),
                productId: booking.product.id,
                pax: booking.participants.pax,
                eventStart: booking.date.start,
                capacityId: booking.capacityId,
                eventId: booking.eventId,
            }),
            [
                booking.participants.pax,
                booking.product.id,
                date,
                booking.date.start,
                booking.capacityId,
                booking.eventId,
            ]
        ),
        open
    );

    const events = useMemo(() => {
        if (!allEvents) return allEvents;
        return allEvents.filter(event => event.id !== booking.eventId);
    }, [allEvents, booking]);

    const errorFields: string[] = [];
    if (!selected) errorFields.push('Event');
    let lastTime: number;

    const { mutate: moveBooking, isLoading: isSaving } = useMoveBooking({
        onSuccess: handleConfirm,
        onError: () => {
            showError('MOVE_BOOKING_FAILURE');
        },
    });

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            PaperProps={{ sx: { py: 1, bgcolor: '#fff', maxWidth: '736px' } }}
        >
            <DialogTitle
                sx={{
                    fontSize: '16px',
                    lineHeight: '16px',
                    fontWeight: 600,
                    color: '#2B395B',
                }}
                id="alert-dialog-title"
            >
                <Typography sx={{ fontWeight: 600 }}>
                    Select booking’s parent event
                </Typography>
                <PriceSync
                    value={syncPricing}
                    onChange={setSyncPricing}
                    sx={{
                        maxWidth: { xs: '100%', sm: '388px' },
                    }}
                />
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        py: 1.5,
                        height: '72px',
                    }}
                >
                    <Box>
                        <DailyViewHeader date={date} />
                    </Box>
                    <Stack>
                        <Form>
                            <FilterItem filter={dateFilter} value={date} />
                        </Form>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    overflow: 'hidden',
                    pr: 1,
                    width: '703px',
                }}
            >
                <Box
                    sx={{
                        pr: 2,
                        height: 'calc(100% - 76px)',
                        '& > div': {
                            maxHeight: 'calc(100vh - 370px)',
                        },
                    }}
                >
                    {!isFetching ? (
                        <PerfectScrollbar>
                            {events?.length ? (
                                <Stack
                                    sx={{
                                        pr: 0.5,
                                        pb: 1,
                                    }}
                                    gap={1}
                                >
                                    {events.map(event => {
                                        const newTime =
                                            lastTime !== event.start;
                                        lastTime = event.start;

                                        return (
                                            <>
                                                {newTime && (
                                                    <Divider
                                                        sx={{
                                                            my: 1,
                                                            '.MuiDivider-wrapper':
                                                                {
                                                                    color: '#9198AA',
                                                                },
                                                        }}
                                                    >
                                                        {format(
                                                            new Date(
                                                                event.start
                                                            ),
                                                            'HH:mm'
                                                        )}
                                                    </Divider>
                                                )}
                                                <EventItemMinimal
                                                    key={event.id}
                                                    event={event}
                                                    selected={
                                                        selected === event
                                                    }
                                                    onSelect={() =>
                                                        setSelected(event)
                                                    }
                                                />
                                            </>
                                        );
                                    })}
                                </Stack>
                            ) : (
                                <Stack
                                    sx={{
                                        display: 'flex',
                                        height: 1,
                                        minHeight: '200px',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                    gap={1}
                                >
                                    <LoopSvg />
                                    <Typography sx={{ color: '#949BAC' }}>
                                        No Events
                                    </Typography>
                                </Stack>
                            )}
                        </PerfectScrollbar>
                    ) : (
                        <Box
                            sx={{
                                py: 2,
                                height: 'calc(100% - 72px - 21px)',
                            }}
                        >
                            <Stack
                                sx={{
                                    px: 2,
                                }}
                                gap={1}
                            >
                                {[0, 1, 2].map(v => (
                                    <EventItemMinimalSkeleton key={v} />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Box>
                {isSaving && (
                    <LoadingOverlay
                        title="Please wait..."
                        subTitle="It’ll just take a moment."
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    color="neutral"
                    variant="contained"
                    onClick={handleCancel}
                    sx={{
                        px: '26px',
                        py: '10px',
                    }}
                >
                    Cancel
                </Button>

                <Button
                    color="secondary"
                    variant="contained"
                    requiredFields={errorFields}
                    onClick={() => {
                        if (selected) {
                            moveBooking({
                                bookingId: booking.id,
                                bookingStatus: booking.status,
                                event: selected,
                                syncPricing,
                            });
                        }
                    }}
                    sx={{
                        py: '10px',
                        px: 4,
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MoveBookingModal;
